import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import api from '@/lib/api';
import { Truck, CheckCircle, AlertCircle, Clock, Send, Package } from 'lucide-react';

export default function SupplierReleasePage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReleaseInfo = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/supplier-release/${token}`);
        setData(response.data);
        setError(null);
      } catch (err) {
        const status = err.response?.status;
        if (status === 410) {
          setError({ type: 'expired', message: 'Dieser Link ist abgelaufen.' });
        } else if (status === 409) {
          setError({ type: 'already_sent', message: 'Lieferanten wurden bereits benachrichtigt.' });
        } else if (status === 400) {
          setError({ type: 'invalid', message: 'Ungültiger Link.' });
        } else if (status === 404) {
          setError({ type: 'not_found', message: 'Bestellung nicht gefunden.' });
        } else {
          setError({ type: 'unknown', message: 'Ein Fehler ist aufgetreten.' });
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchReleaseInfo();
  }, [token]);

  const handleConfirm = async () => {
    try {
      setSending(true);
      const response = await api.post(`/supplier-release/${token}/confirm`);
      // Direkt den "already_sent"-State setzen – zeigt sofort die grüne Bestätigungskarte
      setData({
        status: 'already_sent',
        message: response.data.message || 'Lieferanten wurden erfolgreich benachrichtigt.',
        order_number: data?.order_number || '',
        sent_at: new Date().toISOString(),
        sent_suppliers: response.data.suppliers || []
      });
      setError(null);
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) {
        // Bereits versendet – zeige den already_sent-Zustand
        setData({
          status: 'already_sent',
          message: 'Lieferanten wurden bereits benachrichtigt.',
          order_number: data?.order_number || '',
          sent_at: ''
        });
        setError(null);
      } else if (status === 410) {
        setError({ type: 'expired', message: 'Dieser Link ist abgelaufen.' });
        setData(null);
      } else {
        setError({ type: 'send_failed', message: 'Versand fehlgeschlagen. Bitte versuchen Sie es erneut.' });
      }
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0E518D] mx-auto mb-4"></div>
          <p className="text-slate-500">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-lg mx-auto">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-[#0E518D]">GB Sylt</h1>
          <p className="text-slate-500 text-sm">Lieferanten-Benachrichtigung</p>
        </div>

        {/* Error State */}
        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  {error.type === 'expired' ? (
                    <Clock className="w-6 h-6 text-red-600" />
                  ) : (
                    <AlertCircle className="w-6 h-6 text-red-600" />
                  )}
                </div>
                <div>
                  <h2 className="font-semibold text-lg text-slate-800">
                    {error.type === 'expired' ? 'Link abgelaufen' : 'Fehler'}
                  </h2>
                  <p className="text-slate-600 mt-1">{error.message}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pending State - Ready to Send */}
        {data && data.status === 'pending' && (
          <Card className="border-amber-200 bg-white">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="flex items-center gap-3 text-[#0E518D]">
                <Truck className="w-6 h-6" />
                Lieferanten-Freigabe
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <Alert className="bg-amber-50 border-amber-200 mb-6">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  {data.message}
                </AlertDescription>
              </Alert>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">Bestellnummer:</span>
                  <span className="font-medium text-slate-800">{data.order_number}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">Anreisedatum:</span>
                  <span className="font-medium text-slate-800">{data.arrival_date}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">Anzahl Produkte:</span>
                  <span className="font-medium text-slate-800">{data.items_count}</span>
                </div>
              </div>

              {data.suppliers && data.suppliers.length > 0 && (
                <div className="mb-6">
                  <p className="text-sm font-medium text-slate-700 mb-2">Betroffene Lieferanten:</p>
                  <div className="bg-slate-50 rounded-sm p-3 space-y-2">
                    {data.suppliers.map((name, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                        <Package className="w-4 h-4 text-[#0E518D]" />
                        {name}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(!data.suppliers || data.suppliers.length === 0) && (
                <Alert className="bg-slate-50 border-slate-200 mb-6">
                  <AlertDescription className="text-slate-600">
                    Für diese Bestellung sind keine Lieferanten zugeordnet. Der Versand wird dennoch protokolliert.
                  </AlertDescription>
                </Alert>
              )}

              <Button 
                onClick={handleConfirm} 
                disabled={sending}
                className="w-full bg-[#0E518D] hover:bg-[#0B4171] text-white py-6 text-base"
                data-testid="confirm-supplier-release"
              >
                {sending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Wird versendet...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Jetzt an Lieferanten senden
                  </>
                )}
              </Button>

              <p className="text-xs text-slate-400 text-center mt-4">
                Dieser Link ist nur einmal nutzbar.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Already Sent State */}
        {data && data.status === 'already_sent' && (
          <Card className="border-green-200 bg-green-50" data-testid="supplier-release-sent">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-lg text-green-800">Bereits versendet</h2>
                  <p className="text-green-700 mt-1">{data.message}</p>
                  {data.order_number && (
                    <p className="text-sm text-green-600 mt-2">
                      Bestellung: {data.order_number}
                    </p>
                  )}
                  {data.sent_at && (
                    <p className="text-sm text-green-600">
                      Versendet am: {new Date(data.sent_at).toLocaleString('de-DE')}
                    </p>
                  )}
                  {data.sent_suppliers && data.sent_suppliers.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-green-800">Benachrichtigte Lieferanten:</p>
                      <ul className="mt-2 space-y-1">
                        {data.sent_suppliers.map((s, i) => (
                          <li key={i} className="text-sm text-green-700 flex items-center gap-2">
                            <Truck className="w-4 h-4" />
                            {s.name} ({s.items_count} Produkte)
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
